import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    InjectionToken,
    linkedSignal,
    signal,
} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {NgbModalOptions} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {catchError, EMPTY, finalize, map, Observable, of, switchMap} from 'rxjs';
import {
    AppValidators,
    BuildingServices,
    TRANSLATE,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {
    InputFileDropZoneComponent,
    InputTextGroupComponent,
    ModalCloser,
} from '@brunata-angular/ui';
import {loadedUserHoldsBuildingService} from '@shared/route-utils';
import {ExportNoteComponent} from '../../shared/ui/export-note/export-note.component';
import {OptionItem} from '../../shared/ui/option-card/option-card.model';
import {OptionSelectComponent} from '../../shared/ui/option-select/option-select.component';
import {SavedExportsService} from '../../saved-exports/saved-exports.service';
import {SftpConnectionsService} from '../sftp-connections.service';
import {SftpConnectionTestComponent} from '../sftp-connection-test/sftp-connection-test.component';
import {
    defaultSftpPort,
    sftpAuthenticationTypes,
    SftpAuthenticationType,
    SftpConnection,
    SftpConnectionCredentials,
    SftpConnectionTestProgress,
} from '../sftp-connection.model';
import {ActionButtonComponent} from '../../shared/ui/action-button/action-button.component';

export const sftpConnectionNameMaxLength = 100;

const hostMaxLength = 255;
const usernameMaxLength = 100;
const baseFolderMaxLength = 255;
const testPathMaxLength = 300;
const lowestPort = 1;
const highestPort = 65535;
const privateKeyMaxBytes = 16_384;
const privateKeyPattern = /-----BEGIN [A-Z0-9 ]*PRIVATE KEY-----/;

export type SftpConnectionModalInput = {
    readonly sftpConnection: SftpConnection | null;
};

export const sftpConnectionModalOptions: NgbModalOptions = {
    centered: true,
    windowClass: 'modal-wide',
};

@Component({
    selector: 'app-sftp-connection-modal',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        TranslatePipe,
        InputTextGroupComponent,
        InputFileDropZoneComponent,
        ExportNoteComponent,
        OptionSelectComponent,
        SftpConnectionTestComponent,
        ActionButtonComponent,
    ],
    templateUrl: './sftp-connection-modal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SftpConnectionModalComponent {
    static readonly inputToken = new InjectionToken<SftpConnectionModalInput>(
        'SftpConnectionModalComponent.input',
    );

    static readonly resultToken = new InjectionToken<ModalCloser<SftpConnection>>(
        'SftpConnectionModalComponent.result',
    );

    private readonly closer = inject(SftpConnectionModalComponent.resultToken);
    protected readonly sftpConnection = inject(SftpConnectionModalComponent.inputToken)
        .sftpConnection;
    protected readonly isSaving = signal(false);

    protected readonly nameMaxLength = sftpConnectionNameMaxLength;
    protected readonly hostMaxLength = hostMaxLength;
    protected readonly usernameMaxLength = usernameMaxLength;
    protected readonly baseFolderMaxLength = baseFolderMaxLength;
    protected readonly testPathMaxLength = testPathMaxLength;
    protected readonly authenticationOptions: ReadonlyArray<OptionItem> = [
        {
            id: 'SSH_KEY',
            titleKey: 'sftpconnection_modal_authentication_ssh_key',
            descriptionKey: 'sftpconnection_modal_authentication_ssh_key_description',
        },
        {
            id: 'PASSWORD',
            titleKey: 'sftpconnection_modal_authentication_password',
            descriptionKey: 'sftpconnection_modal_authentication_password_description',
        },
    ];

    protected readonly authenticationType = signal<SftpAuthenticationType>(
        this.sftpConnection?.authenticationType ?? 'SSH_KEY',
    );
    // Switching authentication method drops the picked key, matching the control reset below.
    protected readonly privateKeyFileName = linkedSignal<SftpAuthenticationType, string | null>({
        source: this.authenticationType,
        computation: () => null,
    });
    protected readonly privateKeyError = linkedSignal<
        SftpAuthenticationType,
        'too-large' | 'not-a-key' | null
    >({
        source: this.authenticationType,
        computation: () => null,
    });
    protected readonly isTestRunning = signal(false);
    protected readonly hasSubmitted = signal(false);

    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly translate = inject(TRANSLATE);
    private readonly translateService = inject(TranslateService);
    private readonly toastrService = inject(ToastrService);
    private readonly sftpConnectionsService = inject(SftpConnectionsService);
    private readonly savedExportsService = inject(SavedExportsService);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly form = this.formBuilder.group({
        name: this.formBuilder.control(this.sftpConnection?.name ?? '', {
            validators: [
                Validators.required,
                AppValidators.noEmptyStringValidator,
                Validators.maxLength(sftpConnectionNameMaxLength),
            ],
        }),
        // Grouped so the connection test can run off the destination fields alone - naming the
        // connection is only needed to save it.
        credentials: this.formBuilder.group({
            host: this.formBuilder.control(this.sftpConnection?.host ?? '', {
                validators: [
                    Validators.required,
                    AppValidators.noEmptyStringValidator,
                    Validators.maxLength(hostMaxLength),
                ],
            }),
            port: this.formBuilder.control(
                (this.sftpConnection?.port ?? defaultSftpPort).toString(10),
                {
                    validators: [
                        Validators.required,
                        AppValidators.integer,
                        Validators.min(lowestPort),
                        Validators.max(highestPort),
                    ],
                },
            ),
            username: this.formBuilder.control(this.sftpConnection?.username ?? '', {
                validators: [
                    Validators.required,
                    AppValidators.noEmptyStringValidator,
                    Validators.maxLength(usernameMaxLength),
                ],
            }),
            privateKey: this.formBuilder.control(''),
            password: this.formBuilder.control(''),
            baseFolder: this.formBuilder.control(this.sftpConnection?.baseFolder ?? '', {
                validators: [
                    Validators.required,
                    AppValidators.noEmptyStringValidator,
                    Validators.maxLength(baseFolderMaxLength),
                ],
            }),
            testPath: this.formBuilder.control(this.sftpConnection?.testPath ?? '', {
                validators: [Validators.maxLength(testPathMaxLength)],
            }),
        }),
    });

    private readonly credentialsGroup = this.form.controls.credentials;

    private readonly credentialsValue = toSignal(this.credentialsGroup.valueChanges, {
        initialValue: this.credentialsGroup.value,
    });

    protected readonly testProgress = linkedSignal<unknown, SftpConnectionTestProgress | null>({
        source: this.credentialsValue,
        computation: () => null,
    });

    protected readonly testPathPlaceholder = computed(
        () => `${(this.credentialsValue().baseFolder ?? '').trim().replace(/\/+$/, '')}/test`,
    );

    private readonly areCredentialsValid = toSignal(
        this.credentialsGroup.statusChanges.pipe(map(status => status === 'VALID')),
        {initialValue: this.credentialsGroup.valid},
    );

    protected readonly isTestDisabled = computed(
        () => this.isTestRunning() || !this.areCredentialsValid(),
    );

    protected readonly titleKey =
        this.sftpConnection === null
            ? 'sftpconnection_create_modal_title'
            : 'sftpconnection_edit_modal_title';

    protected readonly submitKey =
        this.sftpConnection === null
            ? 'sftpconnection_create_modal_submit'
            : 'sftpconnection_edit_modal_submit';

    private readonly usedByExportNames = toSignal(this.loadUsedByExportNames(), {
        initialValue: [],
    });

    protected readonly usedByNote = computed(() => {
        const names = this.usedByExportNames();
        if (names.length === 0) {
            return null;
        }
        return names.length === 1
            ? this.translate('sftpconnection_modal_used_by_one', 'online', 'export', names)
            : this.translate('sftpconnection_modal_used_by_many', 'online', 'export', [
                  names.length.toString(10),
                  names.join(', '),
              ]);
    });

    // A saved connection keeps its secret, so re-entering one is only mandatory while creating, while
    // switching authentication method, or while pointing the connection at another server login.
    protected readonly isSecretRequired = computed(() => {
        const stored = this.sftpConnection;
        if (stored === null) {
            return true;
        }
        const {host = '', port = '', username = ''} = this.credentialsValue();
        return (
            this.authenticationType() !== stored.authenticationType ||
            host.trim() !== stored.host ||
            Number(port) !== stored.port ||
            username.trim() !== stored.username
        );
    });

    protected readonly privateKeyState = computed<'empty' | 'missing' | 'picked' | 'stored'>(() => {
        if (this.privateKeyFileName() !== null) {
            return 'picked';
        }
        if (!this.isSecretRequired()) {
            return 'stored';
        }
        return this.hasSubmitted() ? 'missing' : 'empty';
    });

    protected readonly privateKeyTitle = computed(() => {
        switch (this.privateKeyState()) {
            case 'picked':
                return this.privateKeyFileName();
            case 'stored':
                return this.translate(
                    'sftpconnection_modal_private_key_stored',
                    'online',
                    'export',
                );
            default:
                return null;
        }
    });

    protected readonly privateKeyBadge = computed(() => {
        switch (this.privateKeyState()) {
            case 'picked':
                return this.translate('quickexports_export_option_selected', 'online', 'export');
            case 'stored':
                return this.translate(
                    'sftpconnection_modal_private_key_stored_badge',
                    'online',
                    'export',
                );
            default:
                return null;
        }
    });

    constructor() {
        effect(() => this.applyAuthenticationType());
        this.closer.guardDismiss(() => !this.isSaving());
    }

    protected selectAuthenticationType(optionId: string): void {
        const authenticationType = sftpAuthenticationTypes.find(type => type === optionId);
        if (authenticationType !== undefined) {
            this.authenticationType.set(authenticationType);
        }
    }

    protected pickPrivateKey(files: File[]): void {
        const file = files.at(0);
        if (file === undefined) {
            return;
        }
        if (file.size > privateKeyMaxBytes) {
            this.rejectPrivateKey('too-large');
            return;
        }
        void file.text().then(
            content =>
                privateKeyPattern.test(content)
                    ? this.applyPrivateKey(file.name, content)
                    : this.rejectPrivateKey('not-a-key'),
            () => this.rejectPrivateKey('not-a-key'),
        );
    }

    protected runTest(): void {
        if (this.isTestDisabled()) {
            return;
        }
        this.isTestRunning.set(true);
        this.testProgress.set(null);
        const testedCredentials = this.credentialsValue();
        this.sftpConnectionsService
            .testSftpConnection({
                sftpConnectionId: this.sftpConnection?.sftpConnectionId ?? null,
                ...this.buildCredentials(),
            })
            .pipe(
                finalize(() => this.isTestRunning.set(false)),
                // The HTTP interceptor already toasts a failed request; a connection that simply
                // does not answer comes back as a normal response with succeeded: false.
                catchError(() => {
                    this.testProgress.set(null);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(progress => {
                if (this.credentialsValue() === testedCredentials) {
                    this.testProgress.set(progress);
                }
            });
    }

    protected submit(): void {
        this.hasSubmitted.set(true);
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const request = {
            name: this.form.controls.name.getRawValue().trim(),
            ...this.buildCredentials(),
        };
        const stored = this.sftpConnection;
        this.isSaving.set(true);
        (stored === null
            ? this.sftpConnectionsService.createSftpConnection(request)
            : this.sftpConnectionsService.updateSftpConnection(stored.sftpConnectionId, request)
        )
            .pipe(
                finalize(() => this.isSaving.set(false)),
                // The HTTP interceptor already toasts the failure, so the modal stays open for a
                // retry.
                catchError(() => EMPTY),
            )
            .subscribe(saved => {
                this.toastrService.success(
                    this.translateService.translate(
                        stored === null
                            ? 'sftpconnection_create_success'
                            : 'sftpconnection_edit_success',
                        'online',
                        'export',
                    ),
                );
                this.closer.close(saved);
            });
    }

    protected dismiss(): void {
        this.closer.dismiss();
    }

    private loadUsedByExportNames(): Observable<string[]> {
        const stored = this.sftpConnection;
        if (stored === null || stored.usedByExportCount === 0) {
            return of([]);
        }
        return loadedUserHoldsBuildingService(BuildingServices.ASE_SAVED_EXPORTS).pipe(
            switchMap(holdsSavedExports =>
                holdsSavedExports ? this.savedExportsService.getSavedExports() : EMPTY,
            ),
            map(page =>
                page.savedExports
                    .filter(savedExport => savedExport.sftpConnectionId === stored.sftpConnectionId)
                    .map(savedExport => savedExport.name),
            ),
            catchError(() => of([])),
        );
    }

    // Only the selected method's secret takes part in validation and in the payload, so switching
    // method cannot leave the other one's leftovers behind.
    private applyAuthenticationType(): void {
        const {privateKey, password} = this.credentialsGroup.controls;
        const [active, inactive] =
            this.authenticationType() === 'SSH_KEY'
                ? [privateKey, password]
                : [password, privateKey];

        inactive.reset('');
        inactive.disable();
        active.setValidators(
            this.isSecretRequired()
                ? [Validators.required, AppValidators.noEmptyStringValidator]
                : [],
        );
        active.enable();
    }

    private buildCredentials(): SftpConnectionCredentials {
        const authenticationType = this.authenticationType();
        const {host, port, username, privateKey, password, baseFolder, testPath} =
            this.credentialsGroup.getRawValue();
        const trimmedTestPath = testPath.trim();
        return {
            host: host.trim(),
            port: Number(port),
            username: username.trim(),
            authenticationType,
            baseFolder: baseFolder.trim(),
            testPath: trimmedTestPath === '' ? null : trimmedTestPath,
            privateKey: authenticationType === 'SSH_KEY' && privateKey !== '' ? privateKey : null,
            password: authenticationType === 'PASSWORD' && password !== '' ? password : null,
        };
    }

    private applyPrivateKey(fileName: string, content: string): void {
        this.credentialsGroup.controls.privateKey.setValue(content);
        this.credentialsGroup.controls.privateKey.markAsTouched();
        this.privateKeyFileName.set(content.trim() === '' ? null : fileName);
        this.privateKeyError.set(null);
    }

    private rejectPrivateKey(error: 'too-large' | 'not-a-key'): void {
        this.applyPrivateKey('', '');
        this.privateKeyError.set(error);
    }
}
